<template>
  <div>
    <div class="tw-relative tw-inline-block">
      <button
        ref="toggleButtonRef"
        @click="showFilters = !showFilters"
        data-filter-toggle
        class="tw-flex tw-text-xs tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-white tw-bg-secondary-500 tw-border tw-border-solid tw-border-secondary-600 tw-rounded-md hover:tw-bg-secondary-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-secondary-500 focus:tw-border-secondary-500"
      >
        <div class="tw-text-white">
          <FilterIcon />
        </div>
        {{ t('common.forms.filters') }}
      </button>
      <Badge
        v-if="activeFilterCount > 0"
        variant="custom"
        class="tw-absolute -tw-top-2 -tw-right-2 tw-h-5 tw-w-5 tw-flex tw-items-center tw-justify-center tw-text-xs tw-bg-red-200 tw-text-red-600 tw-rounded-full"
      >
        {{ activeFilterCount }}
      </Badge>
    </div>

    <!-- Backdrop + Offcanvas are teleported to <body> so that ancestor
         `contain`/`overflow:hidden` containers (e.g. EntitySplitLayout panes)
         do not become the containing block or clip the fixed-position panel. -->
    <Teleport to="body">
      <!-- Backdrop -->
      <Transition
        enter-active-class="tw-transition-opacity tw-duration-300"
        leave-active-class="tw-transition-opacity tw-duration-300"
        enter-from-class="tw-opacity-0"
        enter-to-class="tw-opacity-100"
        leave-from-class="tw-opacity-100"
        leave-to-class="tw-opacity-0"
      >
        <div
          v-if="showFilters"
          class="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-50 tw-z-[150]"
          @click="showFilters = false"
        ></div>
      </Transition>

      <!-- Offcanvas Panel -->
      <Transition
        enter-active-class="tw-transition-transform tw-duration-300 tw-ease-in-out"
        leave-active-class="tw-transition-transform tw-duration-300 tw-ease-in-out"
        enter-from-class="tw-translate-x-full"
        enter-to-class="tw-translate-x-0"
        leave-from-class="tw-translate-x-0"
        leave-to-class="tw-translate-x-full"
      >
        <div
          v-if="showFilters"
          ref="offcanvasRef"
          class="tw-fixed tw-top-0 tw-right-0 tw-h-full tw-w-96 tw-bg-white tw-shadow-xl tw-z-[200] tw-flex tw-flex-col"
        >
          <!-- Header -->
          <div
            class="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-gray-200 tw-bg-gray-50"
          >
            <div class="tw-flex tw-items-center tw-gap-2 tw-justify-center">
              <h2 class="tw-text-lg tw-font-semibold tw-text-gray-900 tw-mb-0">
                {{ t('common.forms.filters') }}
              </h2>
              <span class="tw-font-normal tw-text-xs tw-text-gray-500">
                ({{ activeFilterCount }} / {{ filterableColumns.length }})
              </span>
            </div>
            <button
              @click="showFilters = false"
              class="tw-p-2 tw-text-gray-400 hover:tw-text-gray-600 tw-rounded-md hover:tw-bg-gray-100 tw-transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="20"
                height="20"
                color="currentColor"
                fill="none"
              >
                <path
                  d="M19.0005 4.99988L5.00049 18.9999M5.00049 4.99988L19.0005 18.9999"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <!-- Fixed Header with Search -->
          <div class="tw-p-4 tw-border-b tw-border-gray-200 tw-bg-white tw-sticky tw-top-0 tw-z-10">
            <h3 class="tw-text-xs tw-text-gray-500 tw-mb-3">
              {{ t('common.forms.column_filters') }}
            </h3>

            <!-- Search Bar -->
            <div class="tw-mb-4 tw-relative">
              <!-- Search Icon -->
              <div
                class="tw-absolute tw-left-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-text-gray-400 tw-pointer-events-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M17.5 17.5L22 22M20 11C20 15.9706 15.9706 20 11 20C6.02944 20 2 15.9706 2 11C2 6.02944 6.02944 2 11 2C15.9706 2 20 6.02944 20 11Z"
                  />
                </svg>
              </div>

              <input
                ref="searchInput"
                v-model="filterSearch"
                :placeholder="t('common.forms.find_column')"
                class="tw-w-full tw-font-normal tw-pl-10 tw-pr-10 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors"
              />

              <!-- Clear Button -->
              <button
                v-if="filterSearch.trim()"
                @click.stop="filterSearch = ''"
                class="tw-absolute tw-right-3 tw-top-1/2 tw-transform -tw-translate-y-1/2 tw-text-gray-400 hover:tw-text-gray-600 tw-transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.5"
                    d="M19 5L5 19M5 5L19 19"
                  />
                </svg>
              </button>
            </div>

            <!-- Results Count -->
            <div v-if="filterSearch.trim()" class="tw-text-xs tw-text-gray-500">
              Showing {{ filteredColumns.length }} of {{ filterableColumns.length }} columns
            </div>
          </div>

          <!-- Scrollable Content -->
          <div class="tw-flex-1 tw-overflow-y-auto tw-p-4 tw-pb-20">
            <div class="tw-space-y-2">
              <div v-for="column in filteredColumns" :key="column.id">
                <label class="tw-text-sm tw-font-medium tw-text-gray-600">
                  {{ column.columnDef.header }}
                </label>

                <!-- Custom filter slot -->
                <slot
                  :name="`filter-${column.id}`"
                  :column="column"
                  :set-filter="(value: any) => column.setFilterValue(value || undefined)"
                  :filter-value="column.getFilterValue()"
                >
                  <!-- Status/Boolean column filter (dropdown) -->
                  <FancyBooleanFilter
                    v-if="(column.columnDef as FancyTableColumnDef<any>).filterType === FilterType.BOOLEAN"
                    :header="{ column } as any"
                    @update:filter="value => column.setFilterValue(value)"
                  />

                  <!-- Date range filter -->
                  <FancyDateRangeFilter
                    v-else-if="
                  column.getCanFilter() &&
                  (column.columnDef as FancyTableColumnDef<any>).fieldType === FieldType.DATE
                "
                    :header="{ column } as any"
                  />

                  <!-- number range filter -->
                  <FancyNumberRangeFilter
                    v-else-if="(column.columnDef as FancyTableColumnDef<any>).filterType === FilterType.NUMBER_RANGE"
                    :header="{ column } as any"
                    :use-currency="(column.columnDef as FancyTableColumnDef<any>).fieldType === FieldType.MONEY"
                    @update:filter="value => column.setFilterValue(value)"
                  />

                  <!-- multi select filter -->
                  <FancyMultiSelectFilter
                    v-else-if="(column.columnDef as FancyTableColumnDef<any>).filterType === FilterType.MULTI_SELECT"
                    :header="{ column } as any"
                    @update:filter="value => column.setFilterValue(value)"
                  />

                  <!-- Regular text input filters -->
                  <input
                    v-else
                    :value="column.getFilterValue() as string"
                    @input="
                      column.setFilterValue(($event.target as HTMLInputElement).value || undefined)
                    "
                    :placeholder="
                      t('common.titles.filter', {
                        columnName: column.columnDef.header,
                      })
                    "
                    class="tw-w-full tw-font-normal tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-secondary-300 tw-focus:border-secondary-300 tw-transition-colors"
                  />
                </slot>
              </div>
            </div>
          </div>

          <!-- Footer with actions -->
          <div
            class="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-p-4 tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-space-y-2"
          >
            <button
              @click.stop="clearAllFilters"
              class="tw-w-full tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 tw-border tw-border-red-200 tw-rounded-md hover:tw-bg-red-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-red-500 tw-transition-colors"
              :disabled="activeFilterCount === 0"
              :class="{
                'tw-opacity-50 tw-cursor-not-allowed': activeFilterCount === 0,
              }"
            >
              {{ t('common.forms.clear_all_filters') }}
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts" generic="T">
import { useI18n } from 'vue-i18n';
import { ref, onMounted, onUnmounted, computed } from 'vue';
import type { Table } from '@tanstack/vue-table';
import type { FancyTableColumnDef } from './types';
import { FieldType, FilterType } from './types';
import FancyBooleanFilter from './Rendering/components/FancyBooleanFilter.vue';
import FancyDateRangeFilter from './Rendering/components/FancyDateRangeFilter.vue';
import FancyNumberRangeFilter from './Rendering/components/FancyNumberRangeFilter.vue';
import FancyMultiSelectFilter from './Rendering/components/FancyMultiSelectFilter.vue';
import FilterIcon from '@/assets/icons/Filter.svg';
import { Badge } from '@/components/ui/badge';

const { t } = useI18n();

const props = defineProps<{
  table: Table<T>;
}>();

const showFilters = ref(false);
const filterSearch = ref('');
const searchInput = ref<HTMLInputElement | null>(null);
const offcanvasRef = ref<HTMLElement | null>(null);
const toggleButtonRef = ref<HTMLElement | null>(null);

// Get all filterable columns
const filterableColumns = computed(() => {
  // Get the current column order from the table state
  const columnOrder = props.table.getState().columnOrder;
  const allColumns = props.table.getAllColumns();

  // Create a map for quick column lookup
  const columnMap = new Map(allColumns.map(col => [col.id, col]));

  // Get visible columns in the correct order
  const visibleColumnsInOrder: any[] = [];

  // First, add columns in the specified order (if any)
  if (columnOrder.length > 0) {
    columnOrder.forEach(columnId => {
      const column = columnMap.get(columnId);
      if (column && column.getCanFilter() && column.getIsVisible()) {
        visibleColumnsInOrder.push(column);
      }
    });
  }

  // Then add any remaining visible filterable columns that weren't in the order
  allColumns.forEach(column => {
    if (column.getCanFilter() && column.getIsVisible() && !visibleColumnsInOrder.includes(column)) {
      visibleColumnsInOrder.push(column);
    }
  });

  return visibleColumnsInOrder;
});

// Filter columns based on search term
const filteredColumns = computed(() => {
  if (!filterSearch.value.trim()) {
    return filterableColumns.value;
  }

  return filterableColumns.value.filter(column => {
    const header = column.columnDef.header?.toString().toLowerCase() || '';
    return header.includes(filterSearch.value.toLowerCase());
  });
});

// Get active filters
const activeFilters = computed(() => {
  return filterableColumns.value
    .filter(column => {
      const filterValue = column.getFilterValue();
      return filterValue !== undefined && filterValue !== null && filterValue !== '';
    })
    .map(column => ({
      id: column.id,
      columnHeader: column.columnDef.header,
      value: column.getFilterValue(),
    }));
});

// Count active filters
const activeFilterCount = computed(() => {
  return activeFilters.value.length;
});

// Clear all filters
const clearAllFilters = (event?: Event) => {
  // Prevent event bubbling to avoid closing the offcanvas
  if (event) {
    event.stopPropagation();
  }

  filterableColumns.value.forEach(column => {
    column.setFilterValue(undefined);
  });
};

// Close offcanvas when clicking outside
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement;
  const offcanvas = offcanvasRef.value;
  const button = toggleButtonRef.value;

  if (offcanvas && !offcanvas.contains(target) && !button?.contains(target)) {
    showFilters.value = false;
  }
};

// Handle keyboard shortcuts
const handleKeyDown = (event: KeyboardEvent) => {
  // Ctrl+F or Cmd+F to focus search when filters are open
  if ((event.ctrlKey || event.metaKey) && event.key === 'f' && showFilters.value) {
    event.preventDefault();
    searchInput.value?.focus();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleKeyDown);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleKeyDown);
});
</script>
<style scoped>
/* Transitions for smooth offcanvas animation */
.tw-transition-transform {
  transition-property: transform;
}

.tw-transition-opacity {
  transition-property: opacity;
}

.tw-duration-300 {
  transition-duration: 300ms;
}

.tw-ease-in-out {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
